import React, { ReactNode, useMemo } from 'react';
import { ModalProps } from '@/type';
import { EditElementForm } from '@/components';
import { useDispatch } from 'react-redux';
import { layoutEditorActions } from '@redux/store/layout-editor/layoutEditorSlice';
import { useSelector } from '@redux/store/store';
import { Box, Drawer, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { styled } from '@mui/system';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { selectedElementSelector } from '@redux/store/layout-editor/layoutEditor.selector';
import {
  layoutEditorLayoutElementQuery,
  layoutEditorLayoutElementQueryFunction,
} from '@/hooks';
import { SuspenseQuery } from '@hclinear/hc-linear-react';

interface EditElementModalProps extends ModalProps {}

const EditElementDrawer = ({ open, handleClose }: EditElementModalProps) => {
  const { t } = useTranslation();
  const { editElementModalConfig, selectedLayoutId, latestUpdate } = useSelector(
    (state) => state.layoutEditor,
  );
  const { isExpanded } = editElementModalConfig;

  const handleDrawerToggle = () => {
    dispatch(layoutEditorActions.toggleEditElementModalExpanded());
  };

  const defaultElement = useSelector(selectedElementSelector);

  const dispatch = useDispatch();

  const drawerWidth = useMemo(() => (isExpanded ? 400 : 25), [isExpanded]);

  const renderForm = (): ReactNode => (
    <Box sx={{ width: '100%' }}>
      <Typography variant='h6' mb={2}>
        {t('layout.edit-element')}
      </Typography>
      <DrawerHandle onClick={handleDrawerToggle}>
        <ChevronRight />
      </DrawerHandle>
      <SuspenseQuery
        queryKey={layoutEditorLayoutElementQuery}
        queryFn={layoutEditorLayoutElementQueryFunction}
      >
        {([fetchedRasterFonts]) => (
          <EditElementForm
            key={latestUpdate + '-' + selectedLayoutId}
            onSubmit={(data) => {
              dispatch(
                layoutEditorActions.updateElementProperty({
                  id: selectedLayoutId,
                  ...data,
                }),
              );
            }}
            fetchedRasterFonts={fetchedRasterFonts}
            element={defaultElement}
          />
        )}
      </SuspenseQuery>
    </Box>
  );

  return (
    <Drawer
      anchor='right'
      open={open}
      onClose={() => handleClose(false)}
      hideBackdrop={true}
      variant='permanent'
      PaperProps={{
        sx: {
          width: drawerWidth,
          minWidth: drawerWidth,
          maxWidth: drawerWidth,
          padding: isExpanded ? 3 : 0,
          boxSizing: 'border-box',
          boxShadow: '0px 0px 10px rgba(0, 0, 0, 0.15)',
          backgroundColor: '#ffffff',
          zIndex: 1200,
          transition: 'width 0.3s ease',
          overflow: 'hidden',
        },
      }}
      sx={{
        '& .MuiBackdrop-root': {
          backgroundColor: 'transparent',
        },
      }}
    >
      {isExpanded ? (
        renderForm()
      ) : (
        <MiniDrawerWrapper className={'mini-drawer'}>
          <DrawerHandle onClick={handleDrawerToggle}>
            <ChevronLeft />
          </DrawerHandle>
        </MiniDrawerWrapper>
      )}
    </Drawer>
  );
};

export default EditElementDrawer;

const MiniDrawerWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: '100%',
  padding: '8px',
}));

const DrawerHandle = styled(Box)(({ theme }) => ({
  position: 'absolute',
  left: 0,
  top: '50%',
  width: 20,
  height: 60,
  backgroundColor: '#ffffff',
  borderRadius: '4px 0 0 4px',
  boxShadow: '-2px 0 4px rgba(0, 0, 0, 0.1)',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '&:hover': {
    backgroundColor: '#f5f5f5',
  },
}));
